import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { Pagination } from '../../components/Pagination';
import CSVExportButton from '../../components/reports/CSVExportButton';

const NOT_IN_EADIN = 'Product Is not in the Eadin';
const NOT_IN_FILE = 'Product Is not in the file';

const COLUMNS = [
    { key: 'supplySku', label: 'Supply SKU', source: 'file' },
    { key: 'productName', label: 'Product Name' },
    { key: 'category', label: 'Category', source: 'file', compareKey: 'eadinCategory' },
    { key: 'subCategory', label: 'Sub Category', source: 'file', compareKey: 'eadinSubcategory' },
    { key: 'status', label: 'Status', source: 'file', compareKey: 'eadinProductStatus' },
    { key: 'forecastable', label: 'Forecastable', source: 'file', compareKey: 'eadinForecastable' },
    { key: 'eadinStatus', label: 'Eadin Status', source: 'eadin' },
    { key: 'eadinCategory', label: 'Eadin Category', source: 'eadin', compareKey: 'category' },
    { key: 'eadinSubcategory', label: 'Eadin Subcategory', source: 'eadin', compareKey: 'subCategory' },
    { key: 'eadinProductStatus', label: 'Eadin Product Status', source: 'eadin', compareKey: 'status' },
    { key: 'eadinForecastable', label: 'Eadin Forecastable', source: 'eadin', compareKey: 'forecastable' },
];

const normalizeValue = (value) => String(value ?? '').trim().toLowerCase();

const valuesDiffer = (left, right) => normalizeValue(left) !== normalizeValue(right);

const isIgnoredComparisonColumn = (column, comparisonOptions = {}) => {
    const { ignoreStatus = false, ignoreForecastable = false } = comparisonOptions;
    const statusKeys = new Set(['status', 'eadinProductStatus']);
    const forecastableKeys = new Set(['forecastable', 'eadinForecastable']);

    if (ignoreStatus && (statusKeys.has(column.key) || statusKeys.has(column.compareKey))) {
        return true;
    }

    if (ignoreForecastable && (forecastableKeys.has(column.key) || forecastableKeys.has(column.compareKey))) {
        return true;
    }

    return false;
};

const getCellClassName = (row, column, comparisonOptions = {}) => {
    if (!row.inEadin && column.source === 'eadin') {
        return 'bg-red-50 text-red-800';
    }

    if (row.inFile === false && column.source === 'file') {
        return 'bg-blue-50 text-blue-800';
    }

    if (
        column.compareKey &&
        row.inEadin &&
        row.inFile !== false &&
        !isIgnoredComparisonColumn(column, comparisonOptions) &&
        valuesDiffer(row[column.key], row[column.compareKey])
    ) {
        return 'bg-amber-50 text-amber-900';
    }

    return 'text-gray-900';
};

const getCellValue = (row, column) => {
    if (row[column.key]) {
        return row[column.key];
    }

    if (column.source === 'eadin' && !row.inEadin) {
        return NOT_IN_EADIN;
    }

    if (column.source === 'file' && row.inFile === false) {
        return NOT_IN_FILE;
    }

    return '';
};

const ExistedSkus = () => {
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({ total: 0, notInEadin: 0, withMismatch: 0, notInFile: 0 });
    const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
    const [currentPage, setCurrentPage] = useState(1);
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(50);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [mismatchesOnly, setMismatchesOnly] = useState(false);
    const [notInEadinOnly, setNotInEadinOnly] = useState(false);
    const [notInFileOnly, setNotInFileOnly] = useState(false);
    const [ignoreStatus, setIgnoreStatus] = useState(false);
    const [ignoreForecastable, setIgnoreForecastable] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const requestIdRef = useRef(0);
    useEffect(() => {
        const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, mismatchesOnly, notInEadinOnly, notInFileOnly, ignoreStatus, ignoreForecastable, currentItemsPerPage]);

    const fetchData = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: String(currentPage),
                pageSize: String(currentItemsPerPage),
            });
            if (debouncedSearch) {
                params.append('search', debouncedSearch);
            }
            if (mismatchesOnly) {
                params.append('mismatchesOnly', 'true');
            }
            if (notInEadinOnly) {
                params.append('notInEadinOnly', 'true');
            }
            if (notInFileOnly) {
                params.append('notInFileOnly', 'true');
            }
            if (ignoreStatus) {
                params.append('ignoreStatus', 'true');
            }
            if (ignoreForecastable) {
                params.append('ignoreForecastable', 'true');
            }
            const response = await axios.get(
                `/api/v1/products/existed-skus-comparison?${params.toString()}`
            );
            if (requestId !== requestIdRef.current) {
                return;
            }
            setData(response.data?.data || []);
            setSummary(
                response.data?.summary || {
                    total: 0,
                    notInEadin: 0,
                    withMismatch: 0,
                    notInFile: 0,
                }
            );
            setPagination(
                response.data?.pagination || {
                    page: 1,
                    pageSize: 50,
                    total: 0,
                    totalPages: 1,
                }
            );
            setError(null);
        } catch (fetchError) {
            if (requestId !== requestIdRef.current) {
                return;
            }
            console.error('Failed to load existed SKUs comparison:', fetchError);
            setData([]);
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [
        currentPage,
        currentItemsPerPage,
        debouncedSearch,
        mismatchesOnly,
        notInEadinOnly,
        notInFileOnly,
        ignoreStatus,
        ignoreForecastable,
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleExportCSV = async () => {
        const params = new URLSearchParams();

        if (debouncedSearch) {
            params.append('search', debouncedSearch);
        }
        if (mismatchesOnly) {
            params.append('mismatchesOnly', 'true');
        }
        if (notInEadinOnly) {
            params.append('notInEadinOnly', 'true');
        }
        if (notInFileOnly) {
            params.append('notInFileOnly', 'true');
        }
        if (ignoreStatus) {
            params.append('ignoreStatus', 'true');
        }
        if (ignoreForecastable) {
            params.append('ignoreForecastable', 'true');
        }

        const query = params.toString();
        const response = await axios.get(
            `/api/v1/products/export-existed-skus-comparison${query ? `?${query}` : ''}`,
            { responseType: 'blob' }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `existed-skus-comparison-${new Date().toISOString().split('T')[0]}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true, handled: true };
    };

    const rangeStart = pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.pageSize) + 1;
    const rangeEnd = Math.min(pagination.page * pagination.pageSize, pagination.total);

    return (
        <main className="w-full h-full px-4 sm:px-6 lg:px-8">
            <div className="space-y-4 my-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Existed SKUs Comparison</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Compare supply SKU data from the existed SKUs file against Eadin product records.
                    </p>
                </div>

                <div className="flex flex-wrap gap-3">
                    <div className="rounded-lg bg-white border border-gray-200 px-4 py-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Total Rows</div>
                        <div className="text-lg font-semibold text-gray-900">{summary.total}</div>
                    </div>
                    <div className="rounded-lg bg-white border border-gray-200 px-4 py-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Not In Eadin</div>
                        <div className="text-lg font-semibold text-red-700">{summary.notInEadin}</div>
                    </div>
                    <div className="rounded-lg bg-white border border-gray-200 px-4 py-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500">With Mismatch</div>
                        <div className="text-lg font-semibold text-amber-700">{summary.withMismatch}</div>
                    </div>
                    {/* <div className="rounded-lg bg-white border border-gray-200 px-4 py-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Not In File</div>
                        <div className="text-lg font-semibold text-blue-700">{summary.notInFile}</div>
                    </div> */}
                </div>

                <div className="flex flex-wrap items-center gap-4 rounded-lg bg-white border border-gray-200 px-4 py-3">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={ignoreStatus}
                            onChange={(event) => setIgnoreStatus(event.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Ignore Status
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={ignoreForecastable}
                            onChange={(event) => setIgnoreForecastable(event.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Ignore Forecastable
                    </label>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search by SKU or product name"
                        className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                        type="button"
                        onClick={() => setMismatchesOnly((value) => {
                            const nextValue = !value;
                            if (nextValue) setNotInFileOnly(false);
                            return nextValue;
                        })}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                            mismatchesOnly
                                ? 'bg-amber-500 hover:bg-amber-400 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                    >
                        {mismatchesOnly ? 'Showing Mismatches Only' : 'Show Mismatches Only'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setNotInEadinOnly((value) => {
                                const nextValue = !value;
                                if (nextValue) setNotInFileOnly(false);
                                return nextValue;
                            });
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                            notInEadinOnly
                                ? 'bg-red-500 hover:bg-red-400 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                    >
                        {notInEadinOnly ? 'Showing Not In Eadin Only' : 'Show Not In Eadin Only'}
                    </button>
                    {/* <button
                        type="button"
                        onClick={() => {
                            setNotInFileOnly((value) => {
                                const nextValue = !value;
                                if (nextValue) {
                                    setNotInEadinOnly(false);
                                    setMismatchesOnly(false);
                                }
                                return nextValue;
                            });
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                            notInFileOnly
                                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                    >
                        {notInFileOnly ? 'Showing Not In File Only' : 'Show Not In File Only'}
                    </button> */}
                    <CSVExportButton
                        columns={COLUMNS}
                        filename="existed-skus-comparison"
                        onExport={handleExportCSV}
                        variant="primary"
                    />
                </div>

                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {isLoading ? (
                    <div className="relative h-64">
                        <LoaderSpinner colorSpinner="black" width="70" height="70" border="5" />
                    </div>
                ) : (
                    <>
                        <div className="bg-white rounded-lg overflow-auto border border-gray-200">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        {COLUMNS.map((column) => (
                                            <th
                                                key={column.key}
                                                className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                            >
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={COLUMNS.length}
                                                className="py-8 px-4 text-center text-sm text-gray-500"
                                            >
                                                No rows match the current filters.
                                            </td>
                                        </tr>
                                    ) : (
                                        data.map((row, index) => (
                                            <tr
                                                key={`${row.supplySku}-${index}`}
                                                className={`border-b border-gray-100 last:border-b-0 ${
                                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                                                }`}
                                            >
                                                {COLUMNS.map((column) => (
                                                    <td
                                                        key={column.key}
                                                        className={`py-3 px-4 text-sm whitespace-nowrap ${getCellClassName(row, column, { ignoreStatus, ignoreForecastable })} ${
                                                            column.key === 'supplySku' ? 'font-mono' : ''
                                                        }`}
                                                    >
                                                        {getCellValue(row, column)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            currentItemsPerPage={currentItemsPerPage}
                            currentPage={currentPage}
                            rangeRows={`${rangeStart}-${rangeEnd} of ${pagination.total}`}
                            isTheLastPage={currentPage >= pagination.totalPages}
                            totalPages={pagination.totalPages}
                            setCurrentPage={setCurrentPage}
                            setCurrentItemsPerPage={setCurrentItemsPerPage}
                        />
                    </>
                )}
            </div>
        </main>
    );
};

export default ExistedSkus;
